import { useState, useEffect, useRef } from 'react';

export const useScrollAnimation = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);
  const observerRef = useRef(null);

  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true,
    delay = 0
  } = options;

  // Mount-gated mobile check. Reading window.innerWidth in the render body
  // produces different output during prerender (Puppeteer's default viewport)
  // vs the user's real viewport, which would break hydration. Defer to an
  // effect so server and first client render agree.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    const setupObserver = () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (delay > 0) {
              setTimeout(() => {
                setIsVisible(true);
                if (triggerOnce) {
                  setHasAnimated(true);
                }
              }, delay);
            } else {
              setIsVisible(true);
              if (triggerOnce) {
                setHasAnimated(true);
              }
            }
          } else if (!triggerOnce && !hasAnimated) {
            setIsVisible(false);
          }
        },
        {
          threshold,
          rootMargin
        }
      );

      const currentElement = elementRef.current;
      if (currentElement) {
        observerRef.current.observe(currentElement);
        
        // Check if element is already visible (fallback for immediate visibility)
        const rect = currentElement.getBoundingClientRect();
        // On mobile, use a more generous visibility check
        const visibilityThreshold = isMobile ? window.innerHeight * 0.8 : window.innerHeight;
        const isCurrentlyVisible = rect.top < visibilityThreshold && rect.bottom > 0;
        
        if (isCurrentlyVisible && !hasAnimated) {
          if (delay > 0) {
            setTimeout(() => {
              setIsVisible(true);
              if (triggerOnce) {
                setHasAnimated(true);
              }
            }, delay);
          } else {
            setIsVisible(true);
            if (triggerOnce) {
              setHasAnimated(true);
            }
          }
        }
      }
    };

    // Initial setup
    if (elementRef.current) {
      setupObserver();
    } else {
      // Retry setup if element is not ready
      const timeoutId = setTimeout(setupObserver, 100);
      return () => clearTimeout(timeoutId);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [threshold, rootMargin, triggerOnce, delay, hasAnimated, isMobile]);

  return [elementRef, isVisible];
};

export const useStaggeredAnimation = (itemCount, options = {}) => {
  const [visibleItems, setVisibleItems] = useState(new Set());
  const elementRef = useRef(null);
  
  const {
    threshold = 0.1,
    staggerDelay = 100,
    triggerOnce = true
  } = options;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Animate items with staggered delay
          for (let i = 0; i < itemCount; i++) {
            setTimeout(() => {
              setVisibleItems(prev => new Set([...prev, i]));
            }, i * staggerDelay);
          }
          
          if (triggerOnce) {
            observer.disconnect();
          }
        }
      },
      { threshold }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [itemCount, staggerDelay, threshold, triggerOnce]);

  return [elementRef, visibleItems];
};
